/**
 * Store-wide "max quantity 1 per variant" guard at the cart level.
 *
 * Quantity selectors are removed from the theme, but a variant could still
 * be duplicated by re-clicking an add button, the sticky promo bar, or a
 * native quick-add form. This script wraps window.fetch: before any
 * /cart/add request goes out, it checks the live cart (/cart.js) and, if
 * any variant in the payload is already in the cart, blocks the add,
 * shows a small "Already in your cart" toast, and returns a synthetic
 * 422 response (same shape as Shopify's own error JSON) so native form
 * components fail gracefully instead of incrementing quantities.
 *
 * Bundles adding distinct variants are unaffected: only variants already
 * present in the cart trigger the block. The zzz-bundle-cart widgets also
 * pre-check duplicates themselves (showing an inline notice), so this
 * guard is the safety net for every other add path.
 *
 * Quantity changes are locked down the same way: /cart/change and
 * /cart/update requests asking for a quantity above 1 are blocked with a
 * "Limited to 1 per item" toast (quantity 0 — removal — always passes),
 * and /cart/add payloads with a quantity above 1 are blocked too.
 *
 * Any error inside the guard fails open — a broken guard must never block
 * legitimate adds or removals.
 */
(() => {
  if (window.__zzzCartGuard) return;
  window.__zzzCartGuard = true;

  const ADD_URL = /\/cart\/add(\.js|\.json)?(\?|$)/;
  const CHANGE_URL = /\/cart\/(change|update)(\.js|\.json)?(\?|$)/;

  const extractVariantIds = (body) => {
    if (!body) return [];
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const id = body.get('id');
      return id ? [Number(id)] : [];
    }
    if (typeof body === 'string') {
      try {
        const data = JSON.parse(body);
        if (Array.isArray(data.items)) return data.items.map((item) => Number(item.id)).filter(Boolean);
        if (data.id) return [Number(data.id)];
      } catch (error) {
        const id = new URLSearchParams(body).get('id');
        if (id) return [Number(id)];
      }
    }
    return [];
  };

  const extractQuantities = (body) => {
    if (!body) return [];
    const quantities = [];
    const collectPairs = (pairs) => {
      for (const [key, value] of pairs) {
        if (key === 'quantity' || key.startsWith('updates')) quantities.push(Number(value));
      }
    };
    if (body instanceof FormData || body instanceof URLSearchParams) {
      collectPairs(body.entries());
      return quantities;
    }
    if (typeof body === 'string') {
      try {
        const data = JSON.parse(body);
        if (data.quantity !== undefined) quantities.push(Number(data.quantity));
        if (Array.isArray(data.items)) data.items.forEach((item) => quantities.push(Number(item.quantity ?? 1)));
        if (data.updates && typeof data.updates === 'object') {
          Object.values(data.updates).forEach((quantity) => quantities.push(Number(quantity)));
        }
      } catch (error) {
        collectPairs(new URLSearchParams(body).entries());
      }
    }
    return quantities;
  };

  const showNotice = (message) => {
    let toast = document.querySelector('.zzz-cart-guard-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'zzz-cart-guard-toast';
      toast.setAttribute('role', 'status');
      toast.style.cssText =
        'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:9999;' +
        'background:var(--color-foreground,#3a3442);color:var(--color-background,#fff);' +
        'padding:10px 18px;border-radius:999px;font-size:0.875rem;' +
        'box-shadow:0 4px 16px rgb(0 0 0 / 0.2);opacity:0;transition:opacity 0.25s ease;pointer-events:none;';
      document.body.append(toast);
    }
    toast.textContent = message;
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
    });
    clearTimeout(toast.__zzzTimer);
    toast.__zzzTimer = setTimeout(() => {
      toast.style.opacity = '0';
    }, 2600);
  };

  const blockedResponse = (message) => {
    // Build the blocking response first: if it can't be built, callers
    // fail open and must not have shown the notice already.
    const blocked = new Response(JSON.stringify({ status: 422, message, description: message }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
    showNotice(message);
    return blocked;
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    if (ADD_URL.test(url) || CHANGE_URL.test(url)) {
      try {
        let body = init?.body;
        if (body === undefined && input instanceof Request) {
          body = await input.clone().text();
        }

        // Quantity 0 is a removal and always passes; anything above 1 is
        // blocked on both add and change/update endpoints.
        const quantities = extractQuantities(body);
        if (quantities.some((quantity) => quantity > 1)) {
          return blockedResponse('Limited to 1 per item');
        }

        if (ADD_URL.test(url)) {
          const ids = extractVariantIds(body);
          if (ids.length) {
            const cart = await originalFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(
              (response) => response.json()
            );
            const inCart = new Set(cart.items.map((item) => item.variant_id));
            if (ids.some((id) => inCart.has(id))) {
              return blockedResponse('Already in your cart');
            }
          }
        }
      } catch (error) {
        /* fail open: never block a legitimate add/removal because the guard broke */
      }
    }
    return originalFetch(input, init);
  };
})();
